import type { RoundConfiguration, StartRoundPayload } from "Models";
import { useSelector } from "react-redux";
import type { QueryState } from "redux-query";
import { type RunMutation, useMutation, useRequest } from "redux-query-react";
import type { EntitiesState, RootState } from "StoreTypes";
import { getFacilityRoundConfigurations } from "./roundConfigurationSelectors";
import { useMemo } from "react";
import {
  getFacilityRoundConfigs,
  makeAddRoundConfigurationMutation,
  makeDeleteRoundConfigurationMutation,
} from "./roundConfigurationQueries";
import type {
  RoundConfigurationToMultiSimsPayload,
  UseDataInfo,
} from "QueryTypes";
import { makeSendRoundConfigurationToSimulatorsMutation } from "./roundConfigurationQueries";

export const useFacilityRoundConfigurations = (
  facilityId: string,
): UseDataInfo<RoundConfiguration[]> => {
  const safeQuery = useMemo(() => {
    if (!facilityId?.trim()) {
      console.log(
        "facilityId is falsy or empty for round configurations query",
      );
      return null;
    }
    return getFacilityRoundConfigs(facilityId);
  }, [facilityId]);

  const [queryState, refreshFacilityRoundConfigurations] = useRequest(
    safeQuery ?? undefined,
  );

  const facilityRoundConfigurations = useSelector(
    (state: RootState) => getFacilityRoundConfigurations(state, facilityId),
    (left, right) => JSON.stringify(left) === JSON.stringify(right),
  );

  return [
    facilityRoundConfigurations,
    queryState,
    refreshFacilityRoundConfigurations,
  ];
};

export const useAddRoundConfiguration = (
  facilityId: string,
): [QueryState, RunMutation<EntitiesState, [StartRoundPayload]>] => {
  const [addRoundQueryState, addRound] = useMutation(
    (body: StartRoundPayload) =>
      makeAddRoundConfigurationMutation(facilityId, body),
  );
  return [addRoundQueryState, addRound];
};

export const useDeleteRoundConfiguration = (
  facilityId: string,
  roundConfigId: string,
): [QueryState, RunMutation<EntitiesState>] => {
  const [deleteRoundQueryState, deleteRound] = useMutation(() =>
    makeDeleteRoundConfigurationMutation(facilityId, roundConfigId),
  );
  return [deleteRoundQueryState, deleteRound];
};

export const useBulkSendRoundConfiguration = (): [
  QueryState,
  RunMutation<EntitiesState, [RoundConfigurationToMultiSimsPayload]>,
] => {
  const [bulkSendRoundConfigurationQueryState, sendRoundConfiguration] =
    useMutation((body: RoundConfigurationToMultiSimsPayload) =>
      makeSendRoundConfigurationToSimulatorsMutation(body),
    );

  return [bulkSendRoundConfigurationQueryState, sendRoundConfiguration];
};
